import { useState, useEffect, useCallback } from 'react';
import BaseballService from '../services/baseballService';

export const useGames = ({ date, league, season, enabled = true }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchGames = useCallback(async () => {
    if (!enabled) return;
    if (!date) return;

    // If no league selected, show empty state (user needs to select a league)
    if (!league) {
      setGames([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Format date as YYYY-MM-DD
      const formattedDate = date instanceof Date ? date.toISOString().split('T')[0] : date;
      // Use America/New_York timezone for MLB games (Eastern Time)
      // This ensures we get the correct games for the US market
      const timezone = 'America/New_York';
      // Use provided season or current year
      const gameSeason = season || new Date().getFullYear();
      
      const response = await BaseballService.getGames(null, formattedDate, league, gameSeason, null, timezone);
      setGames(response.response || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch games');
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, [date, league, season, enabled]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  return { games, loading, error, refetch: fetchGames };
};

// Scalable hook for fetching all season games with batching and caching
export const useSeasonGames = ({ league, season, teamId, enabled = true }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const fetchSeasonGames = useCallback(async () => {
    if (!enabled) return;
    if (!league || !season) return;
    
    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: 0 });
    
    try {
      // Check cache first
      const cacheKey = `season_games_${league}_${season}_${teamId || 'all'}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Use cache if less than 1 hour old
        if (Date.now() - timestamp < 3600000) {
          setGames(data);
          setLoading(false);
          return;
        }
      }

      const timezone = 'America/New_York';
      const allGames = [];
      const batchSize = 7; // Fetch 7 days at a time
      const maxConcurrent = 3; // Max concurrent requests
      
      // Determine date range based on current date and season
      const now = new Date();
      let startDate, endDate;
      
      if (season === now.getFullYear()) {
        // Current season: fetch from season start to today
        startDate = new Date(season, 3, 1); // April 1st
        endDate = now;
        
        // If before season start, show spring training (March)
        if (now.getMonth() < 3) {
          startDate = new Date(season, 2, 1); // March 1st
        }
      } else if (season < now.getFullYear()) {
        // Past season: fetch entire season
        startDate = new Date(season, 3, 1); // April 1st
        endDate = new Date(season, 9, 31); // October 31st
      } else {
        // Future season: no games yet
        setGames([]);
        setLoading(false);
        return;
      }
      
      // Calculate total days to fetch
      const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      setProgress({ current: 0, total: totalDays });
      
      // Create date batches
      const dateBatches = [];
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const batch = [];
        for (let i = 0; i < batchSize && currentDate <= endDate; i++) {
          batch.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        if (batch.length > 0) {
          dateBatches.push(batch);
        }
      }
      
      // Process batches with concurrency limit
      for (let i = 0; i < dateBatches.length; i += maxConcurrent) {
        const batchPromises = dateBatches
          .slice(i, i + maxConcurrent)
          .map(async (batch) => {
            const batchGames = [];
            for (const date of batch) {
              const formattedDate = date.toISOString().split('T')[0];
              try {
                const response = await BaseballService.getGames(
                  null, 
                  formattedDate, 
                  league, 
                  season, 
                  teamId, 
                  timezone
                );
                if (response.response && response.response.length > 0) {
                  batchGames.push(...response.response);
                }
                setProgress(prev => ({ ...prev, current: prev.current + 1 }));
              } catch (err) {
                console.warn(`Failed to fetch games for ${formattedDate}:`, err.message);
              }
            }
            return batchGames;
          });
        
        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach(batchGames => {
          allGames.push(...batchGames);
        });
      }
      
      // Remove duplicates (same game might appear multiple times)
      const uniqueGames = Array.from(
        new Map(allGames.map(game => [game.fixture?.id || game.id, game])).values()
      );
      
      // Sort by date
      uniqueGames.sort((a, b) => {
        const dateA = new Date(a.fixture?.date || a.date);
        const dateB = new Date(b.fixture?.date || b.date);
        return dateA - dateB;
      });
      
      // Cache the results
      localStorage.setItem(cacheKey, JSON.stringify({
        data: uniqueGames,
        timestamp: Date.now()
      }));
      
      setGames(uniqueGames);
    } catch (err) {
      setError(err.message || 'Failed to fetch season games');
      setGames([]);
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  }, [league, season, teamId, enabled]);

  useEffect(() => {
    fetchSeasonGames();
  }, [fetchSeasonGames]);

  return { games, loading, error, progress, refetch: fetchSeasonGames };
};

export const useStandings = ({ league, season }) => {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStandings = useCallback(async () => {
    // If no league selected, show empty state
    if (!league || !season) {
      setStandings([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await BaseballService.getStandings(league, season);
      setStandings(response.response || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch standings');
      setStandings([]);
    } finally {
      setLoading(false);
    }
  }, [league, season]);

  useEffect(() => {
    fetchStandings();
  }, [fetchStandings]);

  return { standings, loading, error, refetch: fetchStandings };
};

export const useTeams = ({ league }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTeams = useCallback(async () => {
    if (!league) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await BaseballService.getTeams(null, null, null, null, league);
      setTeams(response.response || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch teams');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [league]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  return { teams, loading, error, refetch: fetchTeams };
};

export const usePlayerStats = ({ playerId, season }) => {
  const [playerStats, setPlayerStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPlayerStats = useCallback(async () => {
    if (!playerId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Note: The API might require different parameters for player stats
      const response = await BaseballService.getPlayerStats(playerId, season);
      setPlayerStats(response);
    } catch (err) {
      setError(err.message || 'Failed to fetch player stats');
      setPlayerStats(null);
    } finally {
      setLoading(false);
    }
  }, [playerId, season]);

  useEffect(() => {
    fetchPlayerStats();
  }, [fetchPlayerStats]);

  return { playerStats, loading, error, refetch: fetchPlayerStats };
};

export const useGameOdds = ({ gameId }) => {
  const [odds, setOdds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOdds = useCallback(async () => {
    if (!gameId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await BaseballService.getOdds(null, null, gameId);
      setOdds(response);
    } catch (err) {
      setError(err.message || 'Failed to fetch odds');
      setOdds(null);
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    fetchOdds();
  }, [fetchOdds]);

  return { odds, loading, error, refetch: fetchOdds };
};

export const useBaseballCache = () => {
  const [cacheSize, setCacheSize] = useState(0);

  const refreshCacheSize = useCallback(() => {
    // Calculate cache size from the cache object
    const cacheKeys = Object.keys(BaseballService.cache || {});
    const size = cacheKeys.reduce((total, key) => {
      const item = BaseballService.cache[key];
      if (item && item.data) {
        // Estimate size based on JSON string length
        return total + JSON.stringify(item).length;
      }
      return total;
    }, 0);
    setCacheSize(size);
  }, []);

  const clearCache = useCallback(() => {
    // Clear the cache by resetting it
    BaseballService.cache = {};
    refreshCacheSize();
  }, [refreshCacheSize]);

  useEffect(() => {
    refreshCacheSize();
  }, [refreshCacheSize]);

  return { cacheSize, clearCache, refreshCacheSize };
};

export const useLeagues = () => {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLeagues = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use getAllLeagues to include both API and international leagues
      const response = await BaseballService.getAllLeagues();
      setLeagues(response.response || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch leagues');
      setLeagues([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  return { leagues, loading, error, refetch: fetchLeagues };
};

export const useSeasons = () => {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSeasons = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await BaseballService.getSeasons();
      setSeasons(response.response || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch seasons');
      setSeasons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  return { seasons, loading, error, refetch: fetchSeasons };
};